import { cloneDeep } from 'lodash-es';
import {
  type DeviceMapping,
  type RecordEntityId,
  type SiteDevices,
  type StationBrand,
} from '@centric-os/types';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import { defineStore, type StateTree } from 'pinia';
import { INTEGRATION_DEFINITIONS, type DeviceMappingKey } from '../../../constants/integrations';

/**
 * Interface representing the state of the DeviceMapping store.
 *
 * @interface State
 * @extends {StateTree}
 * @property {StationBrand | null} editStation - The currently edited station brand.
 * @property {boolean} isEdit - Flag indicating if the station is being edited.
 * @property {SiteDevices} siteDevices - Devices available at the site.
 * @property {RecordEntityId | null} businessUnitId - The business unit ID associated with the site.
 * @property {boolean} isIntegrationFailed - Flag indicating if the integration has failed.
 */
interface State extends StateTree {
  editStation: StationBrand | null;
  isEdit: boolean;
  siteDevices: SiteDevices;
  businessUnitId: RecordEntityId | null;
  isIntegrationFailed: boolean;
}

/**
 * Creates and returns a Pinia store for managing device mappings.
 *
 * @function useDeviceMappingStore
 * @param {string} [storeId='deviceMapping'] - The unique ID for the store.
 * @returns {import('pinia').Store} The Pinia store instance.
 */
export function useDeviceMappingStore(storeId = 'deviceMapping') {
  const store = defineStore(storeId, {
    state: (): State => ({
      editStation: null,
      isEdit: false,
      siteDevices: null,
      businessUnitId: null,
      isIntegrationFailed: false,
    }),
    actions: {
      /**
       * Fetches the devices for a specific site and updates the state.
       *
       * @async
       * @function getSiteDevices
       * @param {RecordEntityId} siteId - The ID of the site to fetch devices for.
       * @param {DeviceMappingKey} deviceMappingKey - Key of the integration's devices in `device_mapping`.
       * @returns {Promise<void>} A promise that resolves when the devices have been fetched.
       */
      async getSiteDevices(
        siteId: RecordEntityId,
        deviceMappingKey?: DeviceMappingKey,
      ): Promise<void> {
        if (deviceMappingKey === INTEGRATION_DEFINITIONS.volante.deviceMappingKey) {
          const siteStore = useSiteStore();
          this.siteDevices = { site_devices: siteStore.site.device_mapping };
          return;
        }
        const response = await this.serviceApi.get_site_devices(String(siteId));
        this.siteDevices = response;
      },

      /**
       * Updates the devices for a specific station.
       *
       * @async
       * @function updateStationDevices
       * @param {RecordEntityId} stationId - The ID of the station to update devices for.
       * @param {Partial<StationBrand>} payload - The payload containing the device mapping to update.
       * @returns {Promise<void>} A promise that resolves when the devices have been updated.
       * @throws {Error} Throws an error if no station is selected.
       */
      async updateStationDevices(
        stationId: RecordEntityId,
        payload: Partial<StationBrand>,
      ): Promise<void> {
        if (payload && stationId) {
          let device_mapping: DeviceMapping = cloneDeep(payload.device_mapping);

          // Check if all keys in device_mapping are null, if yes, set device_mapping to null
          const allKeysNull = Object.keys(device_mapping).every(
            (key) => device_mapping[key] === null,
          );

          if (allKeysNull) {
            device_mapping = null;
          }

          const finalPayload: Partial<StationBrand> = {
            device_mapping,
          };

          // @ts-expect-error FIXME: hand-maintained StationBrand vs SDK Brand body (TS2345)
          await this.serviceApi.patch_location_brand(String(stationId), finalPayload);
        } else {
          return Promise.reject(new Error('no station selected'));
        }
      },
    },
    getters: {},
    pagination: false,
  });
  return store();
}
